import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { fetchMe, loginCustomer, logoutCustomer, registerCustomer } from '../api/customer';

const STORAGE_TOKEN = 'elenard_customer_token_v1';
const STORAGE_CUSTOMER = 'elenard_customer_cache_v1';

const CustomerAuthContext = createContext(null);

function readStored() {
  if (typeof window === 'undefined') return { token: null, customer: null };
  try {
    const token = window.localStorage.getItem(STORAGE_TOKEN) || null;
    const raw = window.localStorage.getItem(STORAGE_CUSTOMER);
    const customer = raw ? JSON.parse(raw) : null;
    return { token, customer };
  } catch {
    return { token: null, customer: null };
  }
}

export function CustomerAuthProvider({ children }) {
  const [{ token, customer }, setState] = useState(() => readStored());
  const [booting, setBooting] = useState(!!readStored().token);

  const persist = useCallback((nextToken, nextCustomer) => {
    setState({ token: nextToken, customer: nextCustomer });
    try {
      if (nextToken) window.localStorage.setItem(STORAGE_TOKEN, nextToken);
      else window.localStorage.removeItem(STORAGE_TOKEN);
      if (nextCustomer) window.localStorage.setItem(STORAGE_CUSTOMER, JSON.stringify(nextCustomer));
      else window.localStorage.removeItem(STORAGE_CUSTOMER);
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (!token) {
      setBooting(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const me = await fetchMe(token);
        if (!cancelled) persist(token, me);
      } catch {
        if (!cancelled) persist(null, null);
      } finally {
        if (!cancelled) setBooting(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, persist]);

  const login = useCallback(
    async (email, password) => {
      const data = await loginCustomer({ email, password });
      persist(data.token, data.customer);
      return data;
    },
    [persist],
  );

  const register = useCallback(
    async ({ name, email, password, phone }) => {
      const data = await registerCustomer({ name, email, password, phone });
      persist(data.token, data.customer);
      return data;
    },
    [persist],
  );

  const logout = useCallback(async () => {
    try {
      if (token) await logoutCustomer(token);
    } catch (_) {
      /* still clear locally */
    }
    persist(null, null);
  }, [token, persist]);

  const refreshCustomer = useCallback(async () => {
    if (!token) return;
    const me = await fetchMe(token);
    persist(token, me);
  }, [token, persist]);

  const value = useMemo(
    () => ({
      token,
      customer,
      isAuthenticated: !!token && !!customer,
      booting,
      login,
      register,
      logout,
      refreshCustomer,
    }),
    [token, customer, booting, login, register, logout, refreshCustomer],
  );

  return <CustomerAuthContext.Provider value={value}>{children}</CustomerAuthContext.Provider>;
}

export function useCustomerAuth() {
  const ctx = useContext(CustomerAuthContext);
  if (!ctx) throw new Error('useCustomerAuth must be used within CustomerAuthProvider');
  return ctx;
}
